import React, { useState, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import {
    getAllCompaniesRequest,
    createCompanyRequest,
    updateCompanyRequest,
    deleteCompanyRequest,
} from '../../redux/actions/companyactions';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import './CompanyManagement.css';
import { Pencil, Trash2, Building2, Globe, Phone, Mail, MapPin } from 'lucide-react';

/* ─── Modal CRUD ───────────────────────────────────────── */
const CompanyModal = ({ isOpen, onClose, onSubmit, editingCompany, loading }) => {
    const { t } = useTranslation();
    const selectedRole = useSelector((state) => state.role?.selectedRole);
    const isSuperAdmin = selectedRole?.code === 'SUPER_ADMIN';
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        website: '',
        logoUrl: '',
        patente: '',
        idFiscal: '',
        ice: '',
        numAffiliation: '',
        numRc: '',
        cimrAdherente: false,
        cimrNumeroAdherent: '',
        cimrProduit: '',
        cimrDateEffet: '',
        regimeRetraite: 'CNSS',
        isActive: true,
        seuilCriticitePoste: 0,
        maxActiveUsers: '',
        accountRetentionDays: '',
        payrollAutoCloseEnabled: false,
        payrollAutoCloseDelayDays: 3,
    });

    useEffect(() => {
        if (editingCompany) {
            setFormData({
                code: editingCompany.code || '',
                name: editingCompany.name || '',
                description: editingCompany.description || '',
                email: editingCompany.email || '',
                phone: editingCompany.phone || '',
                address: editingCompany.address || '',
                city: editingCompany.city || '',
                country: editingCompany.country || '',
                website: editingCompany.website || '',
                logoUrl: editingCompany.logoUrl || '',
                patente: editingCompany.patente || '',
                idFiscal: editingCompany.idFiscal || '',
                ice: editingCompany.ice || '',
                numAffiliation: editingCompany.numAffiliation || '',
                numRc: editingCompany.numRc || '',
                cimrAdherente: Boolean(editingCompany.cimrAdherente),
                cimrNumeroAdherent: editingCompany.cimrNumeroAdherent || '',
                cimrProduit: editingCompany.cimrProduit || '',
                cimrDateEffet: editingCompany.cimrDateEffet || '',
                regimeRetraite: editingCompany.regimeRetraite || 'CNSS',
                isActive: editingCompany.isActive !== undefined ? editingCompany.isActive : true,
                seuilCriticitePoste: editingCompany.seuilCriticitePoste || 0,
                maxActiveUsers: editingCompany.maxActiveUsers ?? '',
                accountRetentionDays: editingCompany.accountRetentionDays ?? '',
                payrollAutoCloseEnabled: !!editingCompany.payrollAutoCloseEnabled,
                payrollAutoCloseDelayDays: editingCompany.payrollAutoCloseDelayDays ?? 3,
            });
        } else {
            setFormData({
                code: '',
                name: '',
                description: '',
                email: '',
                phone: '',
                address: '',
                city: '',
                country: '',
                website: '',
                logoUrl: '',
                patente: '',
                idFiscal: '',
                ice: '',
                numAffiliation: '',
                numRc: '',
                cimrAdherente: false,
                cimrNumeroAdherent: '',
                cimrProduit: '',
                cimrDateEffet: '',
                regimeRetraite: 'CNSS',
                isActive: true,
                seuilCriticitePoste: 0,
                maxActiveUsers: '',
                accountRetentionDays: '',
                payrollAutoCloseEnabled: false,
                payrollAutoCloseDelayDays: 3,
            });
        }
    }, [editingCompany, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((p) => ({
            ...p,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.code && !formData.code.trim()) {
            Swal.fire({ icon: 'warning', text: t('Le code de la société est obligatoire') });
            return;
        }
        if (!formData.name && !formData.name.trim()) {
            Swal.fire({ icon: 'warning', text: t('Le nom de la société est obligatoire') });
            return;
        }
        if (isSuperAdmin && (!formData.accountRetentionDays || Number(formData.accountRetentionDays) <= 0)) {
            Swal.fire({ icon: 'warning', text: t('La durée de rétention des comptes (jours) est obligatoire') });
            return;
        }

        onSubmit({
            code: formData.code.trim(),
            name: formData.name.trim(),
            description: formData.description ? formData.description.trim() : '',
            email: formData.email ? formData.email.trim() : '',
            phone: formData.phone ? formData.phone.trim() : '',
            address: formData.address ? formData.address.trim() : '',
            city: formData.city ? formData.city.trim() : '',
            country: formData.country ? formData.country.trim() : '',
            website: formData.website ? formData.website.trim() : '',
            logoUrl: formData.logoUrl ? formData.logoUrl.trim() : '',
            patente: formData.patente ? formData.patente.trim() : '',
            idFiscal: formData.idFiscal ? formData.idFiscal.trim() : '',
            ice: formData.ice ? formData.ice.trim() : '',
            numAffiliation: formData.numAffiliation ? formData.numAffiliation.trim() : '',
            numRc: formData.numRc ? formData.numRc.trim() : '',
            cimrAdherente: Boolean(formData.cimrAdherente),
            cimrNumeroAdherent: formData.cimrAdherente && formData.cimrNumeroAdherent ? formData.cimrNumeroAdherent.trim() : null,
            cimrProduit: formData.cimrAdherente ? formData.cimrProduit || null : null,
            cimrDateEffet: formData.cimrAdherente ? formData.cimrDateEffet || null : null,
            regimeRetraite: formData.regimeRetraite || 'CNSS',
            isActive: !!formData.isActive,
            seuilCriticitePoste: parseFloat(formData.seuilCriticitePoste) || 0,
            maxActiveUsers: isSuperAdmin && formData.maxActiveUsers !== '' ? Number(formData.maxActiveUsers) : null,
            accountRetentionDays: isSuperAdmin && formData.accountRetentionDays !== '' ? Number(formData.accountRetentionDays) : null,
            payrollAutoCloseEnabled: isSuperAdmin ? !!formData.payrollAutoCloseEnabled : null,
            payrollAutoCloseDelayDays: isSuperAdmin ? Number(formData.payrollAutoCloseDelayDays) : null,
        });
    };

    if (!isOpen) return null;

    return (
        <div className="cm-overlay" onClick={onClose}>
            <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="cm-modal-header">
                    <h3>{editingCompany ? t('Modifier la société') : t('Ajouter une société')}</h3>
                    <button className="cm-modal-close" onClick={onClose} type="button">✕</button>
                </div>

                <form className="cm-form" onSubmit={handleSubmit}>
                    {/* Section identité */}
                    <div className="cm-section-title">{t('Identité')}</div>
                    <div className="cm-grid">
                        <div className="cm-field">
                            <label>{t('Code')} *</label>
                            <input
                                name="code"
                                value={formData.code}
                                onChange={handleChange}
                                required
                                maxLength={50}
                                placeholder="Ex : BSC-001"
                            />
                        </div>
                        <div className="cm-field">
                            <label>{t('Nom de la société')} *</label>
                            <input
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                maxLength={200}
                                placeholder="Ex : Blue Scratch SARL"
                            />
                        </div>
                        <div className="cm-field cm-full">
                            <label>{t('Description')}</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={2}
                                placeholder={t('Description de la société...')}
                            />
                        </div>
                        <div className="cm-field">
                            <label>{t('URL du logo')}</label>
                            <input
                                name="logoUrl"
                                value={formData.logoUrl}
                                onChange={handleChange}
                                placeholder="https://..."
                            />
                        </div>
                        <div className="cm-field">
                            <label>{t('Site web')}</label>
                            <input
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder="https://..."
                            />
                        </div>
                    </div>

                    <div className="cm-section-title" style={{ marginTop: 14 }}>{t('Informations légales')}</div>
                    <div className="cm-grid">
                        <div className="cm-field">
                            <label>{t('Patente')}</label>
                            <input name="patente" value={formData.patente} onChange={handleChange} maxLength={50} />
                        </div>
                        <div className="cm-field">
                            <label>{t('Identifiant fiscal')}</label>
                            <input name="idFiscal" value={formData.idFiscal} onChange={handleChange} maxLength={50} />
                        </div>
                        <div className="cm-field">
                            <label>{t('ICE')}</label>
                            <input name="ice" value={formData.ice} onChange={handleChange} maxLength={50} />
                        </div>
                        <div className="cm-field">
                            <label>{t('N° d’affiliation')}</label>
                            <input name="numAffiliation" value={formData.numAffiliation} onChange={handleChange} maxLength={50} />
                        </div>
                        <div className="cm-field">
                            <label>{t('N° RC')}</label>
                            <input name="numRc" value={formData.numRc} onChange={handleChange} maxLength={50} />
                        </div>
                    </div>

                    <div className="cm-section-title" style={{ marginTop: 14 }}>Régime de retraite principal</div>
                    <div className="cm-grid">
                        <div className="cm-field">
                            <label>Régime de retraite</label>
                            <select name="regimeRetraite" value={formData.regimeRetraite} onChange={handleChange}>
                                <option value="CNSS">CNSS</option>
                                <option value="CMR">CMR</option>
                            </select>
                        </div>
                    </div>

                    <div className="cm-section-title" style={{ marginTop: 14 }}>CIMR — retraite complémentaire</div>
                    <div className="cm-grid">
                        <div className="cm-field cm-full">
                            <label className="cm-check-label">
                                <input
                                    type="checkbox"
                                    name="cimrAdherente"
                                    checked={Boolean(formData.cimrAdherente)}
                                    onChange={handleChange}
                                />
                                Entreprise adhérente à la CIMR
                            </label>
                        </div>
                        {formData.cimrAdherente && (
                            <>
                                <div className="cm-field">
                                    <label>N° adhérent CIMR</label>
                                    <input name="cimrNumeroAdherent" value={formData.cimrNumeroAdherent} onChange={handleChange} maxLength={50} />
                                </div>
                                <div className="cm-field">
                                    <label>Produit / régime CIMR</label>
                                    <select name="cimrProduit" value={formData.cimrProduit} onChange={handleChange}>
                                        <option value="">Sélectionner</option>
                                        <option value="AL_KAMIL">Al Kamil</option>
                                        <option value="AL_MOUNASSIB">Al Mounassib</option>
                                    </select>
                                </div>
                                <div className="cm-field">
                                    <label>Date d’effet de l’adhésion</label>
                                    <input name="cimrDateEffet" type="date" value={formData.cimrDateEffet} onChange={handleChange} />
                                </div>
                                <div className="cm-field cm-full">
                                    <small>Les profils CIMR se configurent dans Paie → Cotisations, onglet CIMR.</small>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Section contact */}
                    <div className="cm-section-title" style={{ marginTop: 14 }}>{t('Contact')}</div>
                    <div className="cm-grid">
                        <div className="cm-field">
                            <label>{t('Email')}</label>
                            <input
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="contact@societe.ma"
                            />
                        </div>
                        <div className="cm-field">
                            <label>{t('Téléphone')}</label>
                            <input
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+212 6xx xxx xxx"
                            />
                        </div>
                        <div className="cm-field cm-full">
                            <label>{t('Adresse')}</label>
                            <input
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Ex : Rue des Orangers, Quartier Guéliz"
                            />
                        </div>
                        <div className="cm-field">
                            <label>{t('Ville')}</label>
                            <input
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                placeholder="Ex : Casablanca"
                            />
                        </div>
                        <div className="cm-field">
                            <label>{t('Pays')}</label>
                            <input
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                placeholder="Ex : Maroc"
                            />
                        </div>
                    </div>

                    {/* Statut et Paramètres RH */}
                    <div className="cm-section-title" style={{ marginTop: 14 }}>{t('Statut et Paramètres RH')}</div>
                    <div className="cm-grid">
                        <div className="cm-field">
                            <label>{t('Seuil de criticité des postes')}</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                name="seuilCriticitePoste"
                                value={formData.seuilCriticitePoste}
                                onChange={handleChange}
                                placeholder="ex: 3.5"
                            />
                        </div>
                        <div className="cm-field cm-full" style={{ marginTop: 12 }}>
                            <label className="cm-check-label">
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleChange}
                                />
                                {t('Société active')}
                            </label>
                        </div>
                    </div>

                    {isSuperAdmin && (
                        <>
                            <div className="cm-section-title" style={{ marginTop: 14 }}>{t('Paramètres des comptes')}</div>
                            <div className="cm-grid">
                                <div className="cm-field">
                                    <label>{t('Nombre maximal de comptes actifs')}</label>
                                    <input name="maxActiveUsers" type="number" min="1" value={formData.maxActiveUsers} onChange={handleChange} />
                                    <small>{t('Laissez vide pour ne pas fixer de limite')}</small>
                                </div>
                                <div className="cm-field">
                                    <label>{t('Rétention des comptes (jours)')}</label>
                                    <input name="accountRetentionDays" type="number" min="1" required value={formData.accountRetentionDays} onChange={handleChange} />
                                    <small>{t('Délai avant signalement pour suppression')}</small>
                                </div>
                                <div className="cm-field">
                                    <label>{t('Clôture automatique de la paie')}</label>
                                    <label className="cm-check-label">
                                        <input
                                            type="checkbox"
                                            name="payrollAutoCloseEnabled"
                                            checked={formData.payrollAutoCloseEnabled}
                                            onChange={handleChange}
                                        />
                                        {t('Activer la clôture automatique')}
                                    </label>
                                </div>
                                <div className="cm-field">
                                    <label>{t('Délai de clôture de la paie (jours)')}</label>
                                    <input
                                        name="payrollAutoCloseDelayDays"
                                        type="number"
                                        min="0"
                                        value={formData.payrollAutoCloseDelayDays}
                                        onChange={handleChange}
                                        disabled={!formData.payrollAutoCloseEnabled}
                                    />
                                    <small>{t('Contrôle quotidien à 02:00 après la fin du mois')}</small>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="cm-modal-actions">
                        <button
                            type="button"
                            className="cm-btn cm-btn-outline"
                            onClick={onClose}
                            disabled={loading}
                        >
                            {t('Annuler')}
                        </button>
                        <button
                            type="submit"
                            className="cm-btn cm-btn-primary"
                            disabled={loading}
                        >
                            {loading
                                ? t('Chargement...')
                                : editingCompany
                                    ? t('Enregistrer')
                                    : t('Créer la société')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ─── Main ─────────────────────────────────────────────── */
const CompanyManagement = ({
                               companies,
                               loading,
                               message,
                               error,
                               getAllCompanies,
                               createCompany,
                               updateCompany,
                               deleteCompany,
                           }) => {
    const { t } = useTranslation();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');

    useEffect(() => {
        getAllCompanies();
    }, [getAllCompanies]);

    const openCreate = () => {
        setEditingCompany(null);
        setIsModalOpen(true);
    };

    const openEdit = (company) => {
        setEditingCompany(company);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingCompany(null);
    };

    const handleSubmit = (formData) => {
        if (editingCompany) {
            updateCompany(editingCompany.id, formData);
        } else {
            createCompany(formData);
        }
        closeModal();
    };

    const handleDelete = (companyId, companyName) => {
        Swal.fire({
            title: t('Supprimer'),
            text: `${t('Supprimer la société')} "${companyName}" ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#334785',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('Oui, supprimer'),
            cancelButtonText: t('Annuler'),
        }).then((res) => {
            if (res.isConfirmed) deleteCompany(companyId);
        });
    };

    const filteredCompanies = useMemo(() => {
        return (companies || []).filter((company) => {
            const q = searchTerm.toLowerCase().trim();
            const code = (company.code || '').toLowerCase();
            const name = (company.name || '').toLowerCase();
            const city = (company.city || '').toLowerCase();
            const country = (company.country || '').toLowerCase();
            const email = (company.email || '').toLowerCase();

            const matchSearch =
                !q ||
                code.includes(q) ||
                name.includes(q) ||
                city.includes(q) ||
                country.includes(q) ||
                email.includes(q);

            const matchFilter =
                activeFilter === 'all'
                    ? true
                    : activeFilter === 'active'
                        ? !!company.isActive
                        : !company.isActive;

            return matchSearch && matchFilter;
        });
    }, [companies, searchTerm, activeFilter]);

    return (
        <div className="cm-page">
            {/* ── Header ───────────────────────── */}
            <div className="cm-header">
                <div>
                    <h2>
                        <Building2 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                        {t('Gestion des sociétés')}
                    </h2>
                    <p className="cm-subtitle">
                        {t('Gérez et organisez les sociétés de votre organisation')}
                    </p>
                </div>
                <div className="cm-header-actions">
                    <button className="cm-btn cm-btn-primary" onClick={openCreate} type="button">
                        + {t('Ajouter une société')}
                    </button>
                </div>
            </div>

            {/* ── Toolbar ──────────────────────── */}
            <div className="cm-toolbar">
                <input
                    className="cm-search"
                    placeholder={t('Rechercher (code, nom, ville...)')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <div className="cm-filter-tabs">
                    {[
                        { key: 'all', label: t('Toutes') },
                        { key: 'active', label: t('Actives') },
                        { key: 'inactive', label: t('Inactives') },
                    ].map((f) => (
                        <button
                            key={f.key}
                            className={`cm-filter-tab ${activeFilter === f.key ? 'active' : ''}`}
                            onClick={() => setActiveFilter(f.key)}
                            type="button"
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                <div className="cm-meta">
                    {loading
                        ? t('Chargement...')
                        : `${filteredCompanies.length} société${filteredCompanies.length !== 1 ? 's' : ''}`}
                </div>
            </div>

            {/* Alerts */}
            {message && <div className="cm-alert cm-alert-success">{message}</div>}
            {error && <div className="cm-alert cm-alert-danger">{error}</div>}

            {/* ── Table ────────────────────────── */}
            <div className="cm-table-wrapper">
                {loading ? (
                    <div className="cm-loading">
                        <div className="cm-spinner" />
                        <span>{t('Chargement...')}</span>
                    </div>
                ) : filteredCompanies.length === 0 ? (
                    <div className="cm-empty">
                        <p>{t('Aucune société trouvée')}</p>
                    </div>
                ) : (
                    <table className="cm-table">
                        <thead>
                        <tr>
                            <th>{t('Logo')}</th>
                            <th>{t('Code')}</th>
                            <th>{t('Nom')}</th>
                            <th>{t('Contact')}</th>
                            <th>{t('Localisation')}</th>
                            <th>{t('Web')}</th>
                            <th>{t('Statut')}</th>
                            <th style={{ width: 100, textAlign: 'right' }}>{t('Actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredCompanies.map((company) => (
                            <tr key={company.id}>
                                {/* Logo */}
                                <td>
                                    {company.logoUrl ? (
                                        <img
                                            src={company.logoUrl}
                                            alt={company.name}
                                            className="cm-logo-img"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    ) : null}
                                    <div
                                        className="cm-logo-placeholder"
                                        style={{ display: company.logoUrl ? 'none' : 'flex' }}
                                    >
                                        {(company.name || 'S').charAt(0).toUpperCase()}
                                    </div>
                                </td>

                                {/* Code */}
                                <td>
                                    <span className="cm-code">{company.code}</span>
                                </td>

                                {/* Nom + description */}
                                <td>
                                    <div className="cm-name">{company.name}</div>
                                    {company.description && (
                                        <div className="cm-desc">{company.description}</div>
                                    )}
                                </td>

                                {/* Contact */}
                                <td>
                                    <div className="cm-contact-stack">
                                        {company.email && (
                                            <span className="cm-contact-item">
                                                    <Mail size={11} />
                                                {company.email}
                                                </span>
                                        )}
                                        {company.phone && (
                                            <span className="cm-contact-item">
                                                    <Phone size={11} />
                                                {company.phone}
                                                </span>
                                        )}
                                        {!company.email && !company.phone && (
                                            <span className="cm-muted">—</span>
                                        )}
                                    </div>
                                </td>

                                {/* Localisation */}
                                <td>
                                    {(company.city || company.country) ? (
                                        <span className="cm-contact-item">
                                                <MapPin size={11} />
                                            {[company.city, company.country].filter(Boolean).join(', ')}
                                            </span>
                                    ) : (
                                        <span className="cm-muted">—</span>
                                    )}
                                </td>

                                {/* Website */}
                                <td>
                                    {company.website ? (
                                        <a
                                            href={company.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="cm-website-link"
                                        >
                                            <Globe size={11} style={{ marginRight: 4 }} />
                                            {company.website.replace(/^https?:\/\//, '')}
                                        </a>
                                    ) : (
                                        <span className="cm-muted">—</span>
                                    )}
                                </td>

                                {/* Statut */}
                                <td>
                                    <button
                                        type="button"
                                        className={`cm-toggle ${company.isActive ? 'on' : 'off'}`}
                                    >
                                        <span className="cm-toggle-knob" />
                                        <span>{company.isActive ? t('Active') : t('Inactive')}</span>
                                    </button>
                                </td>

                                {/* Actions */}
                                <td className="cm-row-actions">
                                    <button
                                        className="cm-link-btn"
                                        onClick={() => openEdit(company)}
                                        title={t('Modifier')}
                                        type="button"
                                    >
                                        <Pencil size={15} />
                                    </button>
                                    <button
                                        className="cm-link-btn danger"
                                        onClick={() => handleDelete(company.id, company.name)}
                                        title={t('Supprimer')}
                                        type="button"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            <CompanyModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                editingCompany={editingCompany}
                loading={loading}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    companies: state.companies.companies || [],
    loading: state.companies.loading,
    message: state.companies.message,
    error: state.companies.error,
});

const mapDispatchToProps = (dispatch) => ({
    getAllCompanies: () => dispatch(getAllCompaniesRequest()),
    createCompany: (data) => dispatch(createCompanyRequest(data)),
    updateCompany: (id, data) => dispatch(updateCompanyRequest(id, data)),
    deleteCompany: (id) => dispatch(deleteCompanyRequest(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyManagement);
